<template>
  <div class="my-page-context-wizard">
    <div class="my-page-context-wizard__wrapper">
      <div
        class="my-page-context-wizard__context -landlord-profile"
        :class="{ '-read-only': !hasMultipleLandlords }"
      >
        <a-Label for-id="landlord-profile-select">{{ translationLandlordProfileLabel }}</a-Label>
        <select
          id="landlord-profile-select"
          class="form-control"
          v-model.number="context.landlordId"
          :disabled="!hasMultipleLandlords"
          @change="setLandlord($event.target.value)"
        >
          <option v-if="hasMultipleLandlords" :value="undefined" disabled>
            {{ translateLandlordSelectboxLabel }}
          </option>

          <option
            v-for="landlordProfile in landlordOptions"
            :key="landlordProfile.value"
            :value="landlordProfile.value"
          >
            {{ landlordProfile.text }}
          </option>
        </select>
      </div>

      <div
        v-if="inputs.isRentalObjectVisible"
        class="my-page-context-wizard__context -rental-object"
        :class="{ '-read-only': !context.landlordId || !hasMultipleRentalObjects }"
      >
        <a-Label for-id="rental-object-select">{{ translationRentalObjectLabel }}</a-Label>
        <select
          id="rental-object-select"
          class="form-control"
          v-model.number="context.rentalObjectId"
          :disabled="!context.landlordId || !hasMultipleRentalObjects"
          @change="setRentalObject($event.target.value)"
        >
          <template v-if="context.landlordId">
            <option v-if="hasMultipleRentalObjects" :value="undefined" disabled>
              {{ translateRentalObjectSelectboxLabel }}
            </option>
            <option
              v-for="rentalObj in rentalObjectOptions"
              :key="rentalObj.value"
              :value="rentalObj.value"
            >
              {{ rentalObj.text }}
            </option>
          </template>
        </select>
      </div>

      <div
        v-if="inputs.isTenancyTypeVisible"
        class="my-page-context-wizard__context -tenancy-type"
        :class="{ '-read-only': !context.rentalObjectId || !hasAtLeastOneTenancy }"
      >
        <a-Label for-id="tenancy-type-select">{{ translationTenancyTypeLabel }}</a-Label>
        <select
          id="tenancy-type-select"
          class="form-control"
          v-model="context.tenancyType"
          :disabled="!context.rentalObjectId || !hasAtLeastOneTenancy"
          @change="setTenancyType($event.target.value)"
        >
          <template v-if="context.rentalObjectId">
            <template v-if="hasAtLeastOneTenancy">
              <option :value="undefined" disabled>{{ translationSelectTenancyType }}</option>
              <option key="existing" value="existing">{{ translationTenancyTypeSelectExisting }}</option>
            </template>
            <option v-if="activity !== 'top'" key="new" value="new">{{ translationTenancyTypeSelectNoTenancy }}</option>
          </template>
        </select>
      </div>

      <div
        v-if="inputs.isTenancyVisible"
        class="my-page-context-wizard__context -tenancy"
        :class="{ '-read-only': !hasAtLeastOneTenancy }"
      >
        <a-Label for-id="tenancy-select">{{ translationTenancyLabel }}</a-Label>
        <select
          id="tenancy-select"
          class="form-control"
          v-model.number="context.tenancyId"
          :disabled="!hasAtLeastOneTenancy"
          @change="context.tenancyId = Number($event.target.value) || undefined"
        >
          <option v-if="hasAtLeastOneTenancy" :value="undefined" disabled>
            {{ translationSelectFromExistingTenanciesInstruction }}
          </option>
          <option
            v-for="tenancy in tenancyOptions"
            :key="tenancy.value"
            :value="tenancy.value"
            :disabled="primaryAction.type === 'depositAccount' && tenancy.isOrganization"
          >
            {{ tenancy.text }}
          </option>
        </select>
      </div>
    </div>

    <p v-if="activity === 'pubomatic' && isRentalObjectCommercial">
      Det er ikke mulig å annonsere <strong>Næringseiendom</strong>. Hvis du ønsker
      å annonsere dette leieobjektet må det oppdateres til en annen boligtype.
    </p>

    <div class="my-page-context-wizard__bottom">
      <ReuseAdCard
        v-if="activity === 'pubomatic' && context.rentalObjectId && !isRentalObjectCommercial"
        :landlord-id="context.landlordId"
        :rental-object-id="context.rentalObjectId"
        :is-loading="isLoadingAd"
        :ad="ad"
        class="my-page-context-wizard__bottom__ad mt-3 mb-4"
      />

      <!-- To move the buttons to the right if no ad is found -->
      <div v-else></div>

      <div class="my-page-context-wizard__buttons">
        <hl-Button variant="secondary" outline @click="restartWizard">{{ translationCancel }}</hl-Button>
        <div>
          <hl-Button
            v-if="secondaryAction"
            class="mr-4"
            @click="emitSecondaryAction"
            :disabled="!secondaryAction.buttonEnabled"
          >
            {{ secondaryAction.buttonText }}
          </hl-Button>
          <hl-Button
            v-if="primaryAction"
            @click="emitPrimaryAction"
            :disabled="
              !primaryAction.buttonEnabled || (activity === 'pubomatic' && isRentalObjectCommercial)
            "
          >
            {{ primaryAction.buttonText }}
          </hl-Button>
        </div>
      </div>
      <div class="my-page-context-wizard__loading" v-if="isLoading">
        <span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import _ from 'lodash';
import Label from '@modules/design-system/components/atoms/labels/Label.vue';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import ReuseAdCard from '@/components/ReuseAdCard.vue';
import i18nCommon from '@modules/i18n/mixins/Common';
import { LandlordApi, InterestManagementApi, LettingApi } from '@modules/husleieapi2';

const contextDefault = {
  landlordId: undefined,
  rentalObjectId: undefined,
  tenancyType: undefined,
  tenancyId: undefined,
};

export default {
  name: 'MyPageContextWizard',
  mixins: [i18nCommon],
  components: {
    'hl-Button': Button,
    'a-Label': Label,
    ReuseAdCard,
  },

  props: {
    inputs: {
      type: Object,
      required: true,
    },
    primaryAction: {
      type: Object,
      required: true,
    },
    secondaryAction: {
      type: Object,
      required: false,
    },
    activity: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      context: _.cloneDeep(contextDefault),
      ad: undefined,
      isLoadingAd: false,
      isRentalObjectCommercial: false,
    };
  },

  beforeDestroy() {
    this.restartWizard();
  },

  computed: {
    ...mapGetters({
      getRentalObjectsLoading: 'getCondensedRentalObjectsLoading',
      getTenanciesLoading: 'getTenanciesLoading',
      getLandlordProfilesMeta: 'getLandlordProfilesMeta',
      getRentalObjects: 'getCondensedRentalObjects',
      getTenancies: 'getTenancies',
    }),

    isLoading() {
      return this.getRentalObjectsLoading || this.getTenanciesLoading;
    },

    // data
    landlordOptions() {
      return this.getLandlordProfilesMeta
        .map(item => ({
          value: item.id,
          text: item.fullName,
        }))
        .sort((lineA, lineB) => lineA.text.localeCompare(lineB.text));
    },
    rentalObjectOptions() {
      return this.getRentalObjects
        .map(item => ({
          value: item.id,
          text: item.name,
        }))
        .sort((lineA, lineB) => lineA.text.localeCompare(lineB.text));
    },
    tenancyOptions() {
      if (this.context.rentalObjectId) {
        return this.getTenancies
          .filter(tenancy => tenancy.status === 'active' || tenancy.status === 'free')
          .filter(tenancy => tenancy.rentalObjectId === this.context.rentalObjectId)
          .map(item => ({
            value: item.id,
            text: item.tenants[0].fullName,
            isOrganization: item.tenants[0].organizationName || item.tenants[0].organizationNumber,
          }))
          .sort((lineA, lineB) => lineA.text.localeCompare(lineB.text));
      }
      return [];
    },
    hasMultipleLandlords() {
      return this.landlordOptions?.length > 1;
    },
    hasMultipleRentalObjects() {
      return this.rentalObjectOptions?.length > 1;
    },
    hasAtLeastOneTenancy() {
      return this.tenancyOptions?.length > 0;
    },
    hasMultipleTenancies() {
      return this.tenancyOptions?.length > 1;
    },
    adImageUrl() {
      return this.ad.media?.length ? this.ad.media[0].mediaLinks[0].url : undefined;
    },

    // Translations
    translationCancel() {
      return this.translate('cancel');
    },
    translateLandlordSelectboxLabel() {
      return this.translate('contextWizzard.landlordProfile.select landlord profile');
    },
    translateRentalObjectSelectboxLabel() {
      return this.translate('contextWizzard.rentalObject.select rental object');
    },
    translationSelectFromExistingTenanciesInstruction() {
      return this.translate('myPage.wizard.select from my existing tenancies');
    },
    translationTenancyTypeSelectExisting() {
      return this.translate('contextWizzard.tenancyType.select existing tenancy');
    },
    translationTenancyTypeSelectNoTenancy() {
      return this.translate('contextWizzard.tenancyType.continue without tenancy');
    },
    translationSelectTenancyType() {
      return this.translate('contextWizzard.tenancyType.select new or existing tenancy');
    },
    translationLandlordProfileLabel() {
      return this.translate('myPage.wizard.landlord profile label');
    },
    translationRentalObjectLabel() {
      return this.translate('myPage.wizard.rental object label');
    },
    translationTenancyTypeLabel() {
      return this.translate('myPage.wizard.tenancy type label');
    },
    translationTenancyLabel() {
      return this.translate('myPage.wizard.tenancy label');
    },
  },
  watch: {
    context: {
      handler(newValue) {
        this.$emit('change', newValue);
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    ...mapActions({
      fetchRentalObjects: 'fetchCondensedRentalObjectList',
      fetchTenancies: 'fetchTenancies',
    }),
    ...mapMutations({
      setRentalObjects: 'setCondensedRentalObjects',
      setTenancies: 'setTenancies',
    }),

    emitPrimaryAction() {
      this.$emit('click:primaryAction', this.context);
    },
    emitSecondaryAction() {
      this.$emit('click:secondaryAction', this.context);
    },
    async setLandlord(landlordId) {
      this.context.landlordId = Number(landlordId);
      this.context.rentalObjectId = undefined;

      if (this.inputs.isRentalObjectVisible) {
        await this.fetchRentalObjects(this.context.landlordId);

        if (this.rentalObjectOptions.length === 1) {
          await this.setRentalObject(this.rentalObjectOptions[0].value);
        }
      }
    },
    async setRentalObject(rentalObjectId) {
      this.context.rentalObjectId = Number(rentalObjectId);
      this.context.tenancyType = undefined;
      this.context.tenancyId = undefined;
      this.ad = undefined;

      if (this.inputs.isTenancyTypeVisible || this.inputs.isTenancyVisible) {
        await this.fetchTenancies(this.context.landlordId);

        if (!this.tenancyOptions.length) {
          this.setTenancyType('new');
        }
      }

      const rentalObj = this.getRentalObjects.find(item => item.id === this.context.rentalObjectId);
      this.isRentalObjectCommercial = rentalObj?.type === 'commercial';

      if (this.activity === 'pubomatic' && !this.isRentalObjectCommercial) {
        this.getExistingAd();
      }
    },
    setTenancyType(tenancyType) {
      this.context.tenancyType = tenancyType;
      this.context.tenancyId = undefined;
    },
    restartWizard() {
      this.context = _.cloneDeep(contextDefault);
      this.setRentalObjects([]);
      this.setTenancies([]);

      this.$emit('click:cancel');
      this.$emit('change', this.context);
    },
    async getExistingLetting() {
      const interestManagementApi = new InterestManagementApi();
      const resp = await interestManagementApi.getLandlordInterestManagements(this.context.landlordId, {
        query: {
          rentalObjectId: this.context.rentalObjectId,
        },
      });

      if (Array.isArray(resp) && resp.length) {
        const lettingApi = new LettingApi();
        const ad = await lettingApi.getLetting(this.context.landlordId, resp[0].ad.id);

        this.ad = {
          type: 'letting',
          lettingId: ad.id,
          imageUrl: ad.media?.length ? ad.media[0].mediaLinks[0].url : undefined,
          header: ad.content[0]?.sectionHeading,
          street: ad.rentalObject.address.street,
          postalZipCode: ad.rentalObject.address.postalZipCode,
          postalArea: ad.rentalObject.address.postalArea,
        };
      }
    },
    async getRentalObjectPropCloudListing() {
      const api = new LandlordApi();
      const resp = await api.getRentalObjectPropCloudListings(
        this.context.landlordId,
        this.context.rentalObjectId,
      );

      if (Array.isArray(resp) && resp.length) {
        const listing = resp[0];

        this.ad = {
          type: 'propCloud',
          propCloudLettingId: listing.id,
          href: listing.link,
          imageUrl: listing.images[0]?.thumbnails?.[0]?.url,
          header: listing.heading,
          address: listing.address,
        };
      }
    },
    async getExistingAd() {
      this.isLoadingAd = true;

      try {
        await this.getExistingLetting();
        if (!this.ad) {
          await this.getRentalObjectPropCloudListing();
        }
      } finally {
        this.isLoadingAd = false;
      }
    },
  },
  created() {
    if (this.landlordOptions.length === 1) {
      this.setLandlord(this.landlordOptions[0].value);
    }
  },
};
</script>

<style scoped>
.my-page-context-wizard__wrapper {
  display: grid;
  grid-template-columns: 1fr;
}

.my-page-context-wizard__wrapper .form-control {
  @apply mt-3 mb-5;
}

.my-page-context-wizard__bottom {
  @apply flex flex-col items-baseline;
}

.my-page-context-wizard__bottom__ad {
  @apply w-full;
}

.my-page-context-wizard__buttons {
  @apply flex w-full justify-end gap-x-4;
}

/* styling "read only" select boxes as uninteractable */
.my-page-context-wizard__context.-read-only select {
  /* for Firefox */
  -moz-appearance: none;
  /* for Chrome */
  -webkit-appearance: none;
  pointer-events: none;
}

.my-page-context-wizard__loading {
  @apply absolute left-0 right-0 top-0 bottom-0 bg-white flex justify-center items-center;
}

@media (min-width: 800px) {
  .my-page-context-wizard__wrapper {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0rem 2rem;
  }
}

@media (min-width: 1195px) {
  .my-page-context-wizard__bottom {
    @apply flex-row justify-between;
  }
  .my-page-context-wizard__bottom__ad {
    @apply max-w-[646px];
  }
  .my-page-context-wizard__bottom__buttons {
    @apply w-[fit-content];
  }
}
</style>
